import { useState, useEffect, useRef } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { ChevronLeft, Receipt, Search, X } from 'lucide-react'
import { useQuery, gql } from 'urql'
import { Button } from '../components/ui/button'
import { Logo } from '../components/logo'
import { Skeleton } from '../components/ui/skeleton'
import { Card, CardContent } from '../components/ui/card'
import { TransactionCard, TransactionData } from '../components/transaction-card'
import { EditTransactionForm } from '../components/edit-transaction-form'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog'
import { getCurrencySymbol } from '../lib/currencies'
import { CATEGORY_DISPLAY } from '../lib/constants'

const GET_TRANSACTIONS_QUERY = gql`
  query GetAllTransactionsWeb($first: Int, $after: String, $search: String, $category: Category, $year: Int, $month: Int, $day: Int) {
    me {
      preferredCurrency
    }
    transactions(first: $first, after: $after, search: $search, category: $category, year: $year, month: $month, day: $day) {
      edges {
        node {
          id
          merchant
          purchasedAt
          total
          receiptUrl
          receiptFilename
          receiptContentType
          receiptSize
          latitude
          longitude
          notes
          items {
            id
            name
            amount
            category
            purchasedAt
            merchant
          }
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

function buildPageTitle(merchant: string | null, category: string | null, year: string | null, month: string | null, day: string | null): string {
  const parts: string[] = []
  if (merchant) parts.push(merchant)
  else if (category) parts.push(CATEGORY_DISPLAY[category] ?? category)

  if (year && month && day) {
    const date = new Date(parseInt(year), parseInt(month) - 1, parseInt(day))
    parts.push(date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }))
  } else if (year && month) {
    parts.push(`${MONTH_NAMES[parseInt(month) - 1]} ${year}`)
  } else if (year) {
    parts.push(year)
  }

  return parts.length > 0 ? parts.join(' · ') : 'All Transactions'
}

export function TransactionsPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()

  const merchant = searchParams.get('merchant')
  const category = searchParams.get('category')
  const dateStr = searchParams.get('date') // YYYY-MM-DD from bar chart click
  const yearStr = dateStr ? dateStr.split('-')[0] : searchParams.get('year')
  const monthStr = dateStr ? dateStr.split('-')[1] : searchParams.get('month')
  const dayStr = dateStr ? dateStr.split('-')[2] : null
  const year = yearStr ? parseInt(yearStr) : undefined
  const month = monthStr ? parseInt(monthStr) : undefined
  const day = dayStr ? parseInt(dayStr) : undefined

  const [cursor, setCursor] = useState<string | undefined>(undefined)
  const [allEdges, setAllEdges] = useState<{ node: TransactionData }[]>([])
  const [selectedTx, setSelectedTx] = useState<TransactionData | null>(null)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const lastDataRef = useRef<unknown>(null)
  const observerTarget = useRef(null)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => setSearch(value), 400)
  }

  useEffect(() => {
    setCursor(undefined)
    setAllEdges([])
    lastDataRef.current = null
  }, [merchant, category, yearStr, monthStr, dayStr, search])

  // When filtering by merchant, pass it as search (backend searches merchant name)
  const [{ data, fetching, error }, reexecute] = useQuery({
    query: GET_TRANSACTIONS_QUERY,
    variables: {
      first: 20,
      after: cursor,
      search: merchant ? merchant : (search || undefined),
      category: category || undefined,
      year,
      month,
      day: day || undefined,
    },
  })

  const currencySymbol = getCurrencySymbol(data?.me?.preferredCurrency || 'USD')
  const hasNextPage = data?.transactions?.pageInfo?.hasNextPage
  const endCursor = data?.transactions?.pageInfo?.endCursor

  useEffect(() => {
    if (!fetching && data?.transactions?.edges) {
      if (!cursor) {
        setAllEdges(data.transactions.edges)
        lastDataRef.current = data
      } else if (data !== lastDataRef.current) {
        lastDataRef.current = data
        setAllEdges((prev) => [...prev, ...data.transactions.edges])
      }
    }
  }, [data, fetching, cursor])

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasNextPage && !fetching && endCursor) {
          setCursor(endCursor)
        }
      },
      { threshold: 0.1 }
    )
    if (observerTarget.current) observer.observe(observerTarget.current)
    return () => observer.disconnect()
  }, [hasNextPage, fetching, endCursor])

  const pageTitle = buildPageTitle(merchant, category, yearStr, monthStr, dayStr)

  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-10 backdrop-blur-md bg-background/80">
        <div className="container mx-auto flex h-16 items-center justify-between px-4 md:px-6 max-w-4xl">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Link to="/dashboard"><Logo /></Link>
          </div>
          <span className="font-semibold">{pageTitle}</span>
          <div className="w-20" />
        </div>
      </header>

      <main className="container mx-auto p-4 md:p-6 max-w-4xl">
        <div className="relative mb-4">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <input
            type="text"
            placeholder="Search by merchant or item…"
            value={searchInput}
            onChange={(e) => handleSearchChange(e.target.value)}
            className="w-full rounded-lg border border-input bg-background pl-9 pr-9 py-2 text-sm outline-none focus:ring-2 focus:ring-ring"
          />
          {searchInput && (
            <button onClick={() => { setSearchInput(''); setSearch('') }} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
              <X className="h-4 w-4" />
            </button>
          )}
        </div>

        {fetching && allEdges.length === 0 && (
          <div className="grid gap-2">
            {[...Array(8)].map((_, i) => (
              <Card key={i} className="overflow-hidden">
                <CardContent className="flex items-start justify-between p-3 gap-4">
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-[150px]" />
                    <Skeleton className="h-3 w-[200px]" />
                    <Skeleton className="h-3 w-[100px]" />
                  </div>
                  <div className="space-y-2 flex flex-col items-end">
                    <Skeleton className="h-5 w-[80px]" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {error && <div className="text-red-500 py-4">Error: {error.message}</div>}

        {!fetching && !error && allEdges.length === 0 && (
          <div className="flex flex-col items-center justify-center py-16 text-center border rounded-lg bg-muted/30">
            <Receipt className="h-10 w-10 mb-3 text-muted-foreground opacity-40" />
            <h3 className="font-semibold text-base mb-1">No transactions found</h3>
            <p className="text-sm text-muted-foreground">
              {search ? `No results for "${search}".` : pageTitle !== 'All Transactions' ? 'No transactions match this filter.' : 'Go back to the dashboard to add your first expense.'}
            </p>
          </div>
        )}

        <div className="grid gap-2">
          {allEdges.map((edge) => {
            const tx = edge.node
            return tx ? (
              <TransactionCard
                key={tx.id}
                transaction={tx}
                currencySymbol={currencySymbol}
                onClick={() => setSelectedTx(tx)}
              />
            ) : null
          })}
        </div>

        {hasNextPage && (
          <div ref={observerTarget} className="flex justify-center pt-4 h-10">
            {fetching && <span className="text-muted-foreground text-sm">Loading more...</span>}
          </div>
        )}
      </main>

      {/* Edit transaction dialog */}
      <Dialog open={!!selectedTx} onOpenChange={(open) => !open && setSelectedTx(null)}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedTx?.merchant ?? 'Edit expense'}</DialogTitle>
          </DialogHeader>
          {selectedTx && (
            <EditTransactionForm
              transaction={selectedTx}
              currencySymbol={currencySymbol}
              onSuccess={() => {
                setSelectedTx(null)
                setCursor(undefined)
                reexecute({ requestPolicy: 'network-only' })
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </div>
  )
}
